import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Spinner } from "@/components/ui/spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Countdown } from "@/components/ui/countdown";
import { RevealOverlay } from "@/components/RevealOverlay";
import { MissionList } from "@/components/MissionList";
import { DailyBonusCard } from "@/components/DailyBonusCard";
import { WeeklyBonusTracker } from "@/components/WeeklyBonusTracker";
import { getRewardIcon } from "@/components/missionConstants";
import {
  useClaimMission,
  useDailyStreakMission,
  useStats,
} from "@/lib/hooks/useApi";
import type { Mission } from "@/lib/types/api";
import { usePrivy } from "@privy-io/react-auth";
import { Fragment, useCallback, useState } from "react";
import { toast } from "sonner";
import { generateTextShadow } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { translateStreakMissionDescription } from "@/lib/streakMissionTranslations";

const missionRouteMap: Record<string, string> = {
  squad: "/home",
  match: "/practice",
  practice: "/practice",
  long: "/practice",
  short: "/practice",
  referral: "/referrals",
  "referral-seeker": "/referrals",
  gacha: "/referrals",
  invite: "/referrals",
  koth: "/koth",
  "koth-win": "/koth",
  "koth-streak": "/koth",
  any: "/rush",
  win: "/rush",
  rush: "/rush",
  "rush-win": "/rush",
  "rush-first": "/rush",
  "rush-multi-round": "/rush",
  tournament: "/tournament",
  "tournament-win": "/tournament",
  "tournament-first": "/tournament",
  "winner-takes-all": "/winner-takes-all",
  "winner-takes-all-win": "/winner-takes-all",
  "winner-takes-all-amateur": "/winner-takes-all",
  "winner-takes-all-semi-pro": "/winner-takes-all",
  "winner-takes-all-pro": "/winner-takes-all",
  "winner-takes-all-win-amateur": "/winner-takes-all",
  "winner-takes-all-win-semi-pro": "/winner-takes-all",
  "winner-takes-all-win-pro": "/winner-takes-all",
};

const urlFirstTypes = new Set(["link", "follow", "retweet"]);

// eslint-disable-next-line react-refresh/only-export-components
export const getMissionRoute = (missionType: string): string | undefined =>
  missionRouteMap[missionType];

// eslint-disable-next-line react-refresh/only-export-components
export const shouldOpenUrlFirst = (missionType: string): boolean =>
  urlFirstTypes.has(missionType);

export default function DailyMission() {
  const { user } = usePrivy();
  const navigate = useNavigate();
  const walletAddress = user?.wallet?.address ?? "";
  const { data: statsData } = useStats(walletAddress);
  const userId = statsData?.userId;

  const { data: streakData, isLoading } = useDailyStreakMission(userId);
  const { mutateAsync: claimMission } = useClaimMission();

  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [isCompletionBonusPending, setIsCompletionBonusPending] =
    useState(false);
  const [isStreakBonusPending, setIsStreakBonusPending] = useState(false);
  const [clickedMissions, setClickedMissions] = useState<
    Record<string, boolean>
  >({});
  const [claimedMission, setClaimedMission] = useState<Mission | null>(null);
  const [isRevealOpen, setIsRevealOpen] = useState(false);

  const missions = streakData?.missions ?? [];
  const completionBonus = streakData?.completionBonus;
  const streakBonus = streakData?.streakBonus;

  const handleMissionClicked = useCallback((missionId: string) => {
    setClickedMissions((prev) => ({ ...prev, [missionId]: true }));
  }, []);

  const handleClaim = useCallback(
    async (missionId: string) => {
      const mission = missions.find((m) => m.id === missionId);
      if (!mission) return;
      setActiveMissionId(missionId);
      try {
        await claimMission({ missionId });
        setClaimedMission(mission);
        setIsRevealOpen(true);
      } catch {
        toast.error("Failed to claim mission. Please try again.");
      } finally {
        setActiveMissionId(null);
      }
    },
    [missions, claimMission],
  );

  const handleStart = useCallback(
    (mission: Mission) => {
      const url = mission.details.url?.trim();
      if (url) {
        if (url.startsWith("/")) navigate({ to: url });
        else if (typeof window !== "undefined")
          window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      const route = getMissionRoute(mission.type);
      if (route) navigate({ to: route });
    },
    [navigate],
  );

  const handleClaimCompletionBonus = useCallback(async () => {
    if (!completionBonus?.missionId) {
      toast.info("Bonus claim coming soon", {
        description: "Backend support for this claim is rolling out.",
      });
      return;
    }
    setIsCompletionBonusPending(true);
    try {
      await claimMission({ missionId: completionBonus.missionId });
      toast.success("Daily completion bonus claimed!");
    } catch {
      toast.error("Failed to claim bonus. Please try again.");
    } finally {
      setIsCompletionBonusPending(false);
    }
  }, [claimMission, completionBonus]);

  const handleClaimStreakBonus = useCallback(async () => {
    if (!streakBonus?.missionId) {
      toast.info("Bonus claim coming soon", {
        description: "Backend support for this claim is rolling out.",
      });
      return;
    }
    setIsStreakBonusPending(true);
    try {
      await claimMission({ missionId: streakBonus.missionId });
      toast.success("Streak bonus claimed!");
    } catch {
      toast.error("Failed to claim streak bonus. Please try again.");
    } finally {
      setIsStreakBonusPending(false);
    }
  }, [claimMission, streakBonus]);

  if (isLoading && !streakData) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!streakData) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-2">
        <p
          className="text-white/70 text-sm text-center"
          style={{ textShadow: generateTextShadow(0.3) }}
        >
          Couldn't load daily missions. Try again later.
        </p>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="flex-1 min-h-0 flex flex-col gap-2 pt-2">
        {/* Completion bonus card — constrained to dialog-ish width so the
            mascot bg art stays centered relative to the content. */}
        {completionBonus && (
          <div className="w-full max-w-md mx-auto">
            <DailyBonusCard
              missionsCompleted={
                missions.filter(
                  (m) => m.isClaimed || (m.isEligible && m.type !== "follow"),
                ).length
              }
              missionsTotal={missions.length}
              isComplete={completionBonus.isComplete}
              isClaimed={completionBonus.isClaimed}
              isPending={isCompletionBonusPending}
              onClaim={handleClaimCompletionBonus}
              variant="daily"
              prize={completionBonus.prize}
              reward={completionBonus.reward}
            />
          </div>
        )}

        {/* Mission list */}
        <ScrollArea className="flex-1 min-h-0 px-2" type="auto">
          <div className="space-y-2 py-1">
            <MissionList
              missions={missions}
              isLoading={isLoading}
              activeMissionId={activeMissionId}
              onClaim={handleClaim}
              onStart={handleStart}
              clickedMissions={clickedMissions}
              onMissionClicked={handleMissionClicked}
            />
          </div>
        </ScrollArea>

        {/* Streak bonus + reset countdown — combined block, no inner divider */}
        {streakBonus && (
          <div className="px-3 pt-2 pb-0 flex flex-col gap-2 border-t-4 border-[#0659B5] shrink-0">
            <div
              className="rounded-sm px-3 py-2"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(/assets/daily-bonus-tracker-bg.png)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                imageRendering: "pixelated",
              }}
            >
              <WeeklyBonusTracker
                streakData={streakBonus}
                onClaim={handleClaimStreakBonus}
                isPending={isStreakBonusPending}
              />
            </div>
            {streakData.resetTimestamp ? (
              <div className="flex items-center justify-between px-1">
                <span
                  className="text-white/85 text-xs"
                  style={{ textShadow: generateTextShadow(1) }}
                >
                  Resets daily
                </span>
                <div className="flex items-center justify-center gap-2 black-container h-8 px-1 opacity-75">
                  <img
                    src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                    className="w-3.5"
                    alt="timer"
                  />
                  <Countdown
                    startTimestamp={streakData.resetTimestamp}
                    className="text-xs"
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <RevealOverlay
        isOpen={isRevealOpen && Boolean(claimedMission)}
        onClose={() => {
          setIsRevealOpen(false);
          setClaimedMission(null);
        }}
        title={t("streakMissions.missionCompleted")}
        rewardText={t("streakMissions.reward")}
        rewardValue={claimedMission?.rewardAmount ?? 0}
        buttonText={t("streakMissions.close")}
        description={
          claimedMission
            ? t("streakMissions.youCompleted", {
                description: translateStreakMissionDescription(
                  claimedMission.description,
                ),
              })
            : undefined
        }
        shouldUseRewardIcons={
          claimedMission?.rewardType !== "free_pass" &&
          claimedMission?.rewardType !== "gacha_coin" &&
          claimedMission?.rewardType !== "lucky_draw"
        }
        alternatePrizeImageSrc={
          claimedMission ? getRewardIcon(claimedMission.rewardType) : undefined
        }
        alternatePrizeImageSrcSize="h-24! w-auto"
        alternatePrizeText={
          claimedMission ? `+${claimedMission.rewardAmount}` : undefined
        }
      />
    </Fragment>
  );
}

export const Route = createFileRoute("/(game)/missions/daily-mission")({
  component: DailyMission,
});
